import React, { useState } from 'react';
import { Box, Button, Modal, TextField, Typography, CircularProgress, Alert } from '@mui/material';
import useLocales from 'hooks/useLocales';
import { saveUnauthorizedUser } from './requests/NonAuthenticatedRequests';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 340,
    bgcolor: 'background.paper',
    borderRadius: 1,
    boxShadow: 24,
    p: 4,
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^(?:\+|0)[0-9]{8,11}$/;

const NonAuthenticatedModal = ({ open, setOpen }: { open: boolean; setOpen: (status: boolean) => void }) => {
    const { translate } = useLocales();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [errors, setErrors] = useState({ name: '', email: '', phone: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const handleSave = async () => {
        const emailValid = emailRegex.test(email);
        const phoneValid = phoneRegex.test(phone);
        const nameValid = name.trim() !== '' && name.trim().length > 2;

        setErrors({
            name: nameValid ? '' : translate('first_name_required'),
            email: emailValid ? '' : translate('email_address_must_be_valid'),
            phone: phoneValid ? '' : translate('phone_number_is_not_valid'),
        });

        if (nameValid && emailValid && phoneValid) {
            setIsSubmitting(true);
            setSubmitError(null);

            try {
                localStorage.setItem('contactUserName', name);
                localStorage.setItem('contactUserEmail', email);
                localStorage.setItem('contactUserPhone', phone);
                setOpen(false);

                await saveUnauthorizedUser(email, name, phone);
            } catch (error: any) {
                setSubmitError(translate('something_went_wrong'));
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <Modal
            open={open}
            BackdropProps={{
                sx: {
                    background: 'rgba(0, 0, 0, 0.6) !important',
                },
            }}
        >
            <Box sx={style}>
                <Typography variant='h6' mb={2}>
                    {translate('fill_data')}
                </Typography>
                <TextField
                    label={translate('first_name')}
                    fullWidth
                    value={name}
                    onChange={e => setName(e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    margin='normal'
                />
                <TextField
                    label={translate('email_address')}
                    fullWidth
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    margin='normal'
                />
                <TextField
                    label={translate('phone_number')}
                    fullWidth
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    margin='normal'
                />
                {submitError && (
                    <Alert severity='error' sx={{ mt: 2, mb: 1 }}>
                        {submitError}
                    </Alert>
                )}
                <Button
                    fullWidth
                    variant='contained'
                    sx={{ mt: 2 }}
                    onClick={handleSave}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    {isSubmitting ? translate('loading') : translate('save')}
                </Button>
            </Box>
        </Modal>
    );
};

export default NonAuthenticatedModal;
